import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  CreditCard,
  TrendingUp,
  DollarSign,
  Clock,
  AlertCircle,
  ArrowRight,
  Loader2,
  PlusCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientDashboardPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, [token]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/client/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      settled: 'bg-blue-100 text-blue-800',
      overdue: 'bg-red-100 text-red-800'
    };
    return variants[status] || variants.active;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#0D3B66]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <AlertCircle className="h-12 w-12 text-red-500" />
        <p className="text-red-600">{error}</p>
        <Button onClick={fetchDashboard}>Retry</Button>
      </div>
    );
  }

  const stats = dashboardData?.statistics || {};
  const profile = dashboardData?.profile || {};
  const recentLoans = dashboardData?.recent_loans || [];

  return (
    <div className="space-y-6" data-testid="client-dashboard">
      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#212529] font-['Manrope']">
            Welcome, {profile.company_name || 'Client'}
          </h1>
          <p className="text-[#6C757D]">Client ID: {profile.client_id || '-'}</p>
        </div>
        <Link to="/client/fund-requests">
          <Button className="bg-[#0D3B66] hover:bg-[#0A2F52]" data-testid="new-fund-request-btn">
            <PlusCircle className="h-4 w-4 mr-2" />
            Request Funds
          </Button>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#6C757D]">Total Outstanding</p>
                <p className="text-2xl font-bold text-[#212529]">{formatCurrency(stats.total_outstanding)}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#6C757D]">Active Loans</p>
                <p className="text-2xl font-bold text-[#212529]">{stats.active_loans || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <CreditCard className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#6C757D]">Available Credit</p>
                <p className="text-2xl font-bold text-[#212529]">{formatCurrency(stats.available_credit)}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#6C757D]">Pending Requests</p>
                <p className="text-2xl font-bold text-[#212529]">{stats.pending_fund_requests || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Credit Limit Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Credit Facility</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1">
              <div className="flex justify-between mb-2">
                <span className="text-sm text-[#6C757D]">Used</span>
                <span className="text-sm font-medium">{formatCurrency(stats.total_outstanding)} / {formatCurrency(stats.credit_limit)}</span>
              </div>
              <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#0D3B66] rounded-full transition-all"
                  style={{ width: `${Math.min((stats.total_outstanding / stats.credit_limit) * 100, 100)}%` }}
                />
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-[#6C757D]">Utilization</p>
              <p className="text-xl font-bold text-[#212529]">
                {stats.credit_limit > 0 ? ((stats.total_outstanding / stats.credit_limit) * 100).toFixed(1) : 0}%
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Loans */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Recent Loans</CardTitle>
          <Link to="/client/loans">
            <Button variant="ghost" size="sm" className="text-[#0D3B66]">
              View All <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {recentLoans.length === 0 ? (
            <p className="text-center text-[#6C757D] py-8">No loans found</p>
          ) : (
            <div className="space-y-4">
              {recentLoans.map((loan) => (
                <div
                  key={loan.loan_number || loan.loan_id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-[#212529]">Loan {loan.loan_number}</p>
                    <p className="text-sm text-[#6C757D]">
                      {loan.deal_type} | {formatDate(loan.purchase_date)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-[#212529]">{formatCurrency(loan.outstanding || loan.closing_balance || loan.original_amount)}</p>
                    <Badge className={getStatusBadge(loan.status)}>
                      {loan.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientDashboardPage;
